/**
 * Hooks per la Gazzetta dello Squad (Andrea 2026-09-24): prime pagine,
 * report, dichiarazioni e Top 11 di una giornata.
 *
 *   GET    /gazzetta/{giornata}                → contenuti della giornata
 *   GET    /gazzetta/{giornata}/top11          → Top 11 + Top manager
 *   POST   /gazzetta/{giornata}/prime-pagine   → immagine (data-URL)
 *   POST   /gazzetta/{giornata}/report         → multipart: titolo + .docx o testo
 *   POST   /gazzetta/{giornata}/dichiarazioni  → testo
 *   DELETE /gazzetta/prime-pagine|report|dichiarazioni/{id}
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface PrimaPaginaItem {
  id: string;
  giornata_id: string;
  user_id: string;
  autore: string;
  squadra_id: string | null;
  titolo: string | null;
  immagine_data_url: string;
  created_at: string;
  /** fatta col generatore: si puo' riaprire e modificare (2026-10-15) */
  ha_dati?: boolean;
}

export interface ReportItem {
  id: string;
  giornata_id: string;
  user_id: string;
  autore: string;
  squadra_id: string | null;
  titolo: string;
  testo: string;
  nome_file: string | null;
  created_at: string;
}

export interface DichiarazioneItem {
  id: string;
  giornata_id: string;
  user_id: string;
  autore: string;
  squadra_id: string;
  testo: string;
  created_at: string;
}

export interface GazzettaGiornata {
  giornata_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
  prime_pagine: PrimaPaginaItem[];
  report: ReportItem[];
  dichiarazioni: DichiarazioneItem[];
  mia_squadra_id: string | null;
  posso_moderare: boolean;
  /** avatar degli autori di report e dichiarazioni (user_id -> data-URL) */
  avatar_autori?: Record<string, string>;
}

export interface GiocatoreTopItem {
  giocatore_id: string;
  nome: string;
  club_serie_a: string | null;
  ruoli_mantra: string[];
  ruolo_classic: string | null;
  slot_ruolo: string;
  voto_base: string;
  fantavoto: string;
  squadra_id: string | null;
  gol_fatti: number;
  gol_subiti: number;
  autogol: number;
  ammonizioni: number;
  espulsioni: number;
  assist: number;
  porta_inviolata: boolean;
  rigore_segnato: number;
  rigore_sbagliato: number;
  rigore_parato: number;
}

export interface UndiciItem {
  modulo: string;
  slot_labels: string[];
  giocatori: GiocatoreTopItem[];
  totale: string;
}

export interface TopManagerItem {
  squadra_id: string;
  nome: string;
  punteggio: string;
  gol: number;
}

export interface Top11Response {
  giornata_id: string;
  disponibile: boolean;
  motivo: string | null;
  serie_a: UndiciItem | null;
  fantasquad: UndiciItem | null;
  top_manager: TopManagerItem[];
}

export function useGazzetta(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["gazzetta", giornataId],
    queryFn: async () => (await api.get<GazzettaGiornata>(`/gazzetta/${giornataId}`)).data,
    enabled: isAuth && !!giornataId,
  });
}

export function useTop11(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["gazzetta", giornataId, "top11"],
    queryFn: async () => (await api.get<Top11Response>(`/gazzetta/${giornataId}/top11`)).data,
    enabled: isAuth && !!giornataId,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidaGazzetta(giornataId: string | null) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["gazzetta", giornataId] });
}

export type DatiGeneratore = Record<string, unknown>;

export function useCaricaPrimaPagina(giornataId: string | null) {
  const invalida = useInvalidaGazzetta(giornataId);
  return useMutation({
    mutationFn: async (args: {
      immagineDataUrl: string;
      titolo?: string;
      squadraId?: string | null;
      dati?: DatiGeneratore | null;
    }) =>
      (
        await api.post<PrimaPaginaItem>(`/gazzetta/${giornataId}/prime-pagine`, {
          immagine_data_url: args.immagineDataUrl,
          titolo: args.titolo || null,
          squadra_id: args.squadraId ?? null,
          dati: args.dati ?? null,
        })
      ).data,
    onSuccess: invalida,
  });
}

/** Andrea 2026-10-15: corregge una prima pagina fatta col generatore. */
export function useAggiornaPrimaPagina(giornataId: string | null) {
  const invalida = useInvalidaGazzetta(giornataId);
  return useMutation({
    mutationFn: async (args: {
      paginaId: string;
      immagineDataUrl: string;
      titolo?: string;
      dati?: DatiGeneratore | null;
    }) =>
      (
        await api.put<PrimaPaginaItem>(`/gazzetta/prime-pagine/${args.paginaId}`, {
          immagine_data_url: args.immagineDataUrl,
          titolo: args.titolo || null,
          dati: args.dati ?? null,
        })
      ).data,
    onSuccess: invalida,
  });
}

/** I campi compilati di una prima pagina (per riaprirla nel generatore). */
export async function caricaDatiPrimaPagina(paginaId: string): Promise<DatiGeneratore | null> {
  const res = await api.get<{ dati: DatiGeneratore | null }>(
    `/gazzetta/prime-pagine/${paginaId}/dati`,
  );
  return res.data.dati;
}

export function useCaricaReport(giornataId: string | null) {
  const invalida = useInvalidaGazzetta(giornataId);
  return useMutation({
    mutationFn: async (args: {
      titolo: string;
      file?: File | null;
      testo?: string;
      squadraId?: string | null;
    }) => {
      const fd = new FormData();
      fd.append("titolo", args.titolo);
      if (args.file) fd.append("file", args.file);
      if (args.testo) fd.append("testo", args.testo);
      if (args.squadraId) fd.append("squadra_id", args.squadraId);
      return (
        await api.post<ReportItem>(`/gazzetta/${giornataId}/report`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    onSuccess: invalida,
  });
}

export function useNuovaDichiarazione(giornataId: string | null) {
  const invalida = useInvalidaGazzetta(giornataId);
  return useMutation({
    mutationFn: async (args: { testo: string; squadraId: string }) =>
      (
        await api.post<DichiarazioneItem>(`/gazzetta/${giornataId}/dichiarazioni`, {
          testo: args.testo,
          squadra_id: args.squadraId,
        })
      ).data,
    onSuccess: invalida,
  });
}

export function useEliminaContenutoGazzetta(giornataId: string | null) {
  const invalida = useInvalidaGazzetta(giornataId);
  return useMutation({
    mutationFn: async (args: { tipo: "prime-pagine" | "report" | "dichiarazioni"; id: string }) =>
      (await api.delete(`/gazzetta/${args.tipo}/${args.id}`)).data,
    onSuccess: invalida,
  });
}
